import { HistoricoDto } from '@/api.types';
import {
  estadoAntesDoEvento,
  jsonDetalheHistorico,
  linhasDiffHistorico,
  LinhaDiffHistorico,
  rotuloTabela,
} from '@/modules/system/historico/historico-detalhe.utils';
import { IconeAcao } from '@/modules/system/historico/historico-icones';
import { GetHistoricoUsuario } from '@/modules/system/historico/historico.service';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

type VisaoDetalhe = 'atual' | 'alteracoes';

const LIMITE_TEXTO_CURTO = 80;
const VISAO_STORAGE_KEY = 'historico.detalhe.visao';

function lerVisaoSalva(): VisaoDetalhe {
  try {
    const salva = localStorage.getItem(VISAO_STORAGE_KEY);
    if (salva === 'atual' || salva === 'alteracoes') return salva;
  } catch {
    /* ignore */
  }
  return 'alteracoes';
}

function salvarVisao(visao: VisaoDetalhe) {
  try {
    localStorage.setItem(VISAO_STORAGE_KEY, visao);
  } catch {
    /* ignore */
  }
}

interface ModalDetalheHistoricoProps {
  item: HistoricoDto | null;
  show: boolean;
  onHide: () => void;
}

function dataLinha(valor: string | Date) {
  return format(new Date(valor), "EEEE dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

function textoCurto(valor: string | undefined): boolean {
  if (valor == null) return true;
  return valor.length <= LIMITE_TEXTO_CURTO && !valor.includes('\n');
}

function linhasEmTabela(linhas: LinhaDiffHistorico[]): boolean {
  return linhas.every((linha) => textoCurto(linha.removido) && textoCurto(linha.adicionado));
}

function VisaoAtual({ log }: { log: HistoricoDto }) {
  const json = jsonDetalheHistorico(log.diff as Record<string, unknown> | null);

  if (log.acao === 'DELETE' && !json) {
    return <p className="mb-0">O registro foi removido.</p>;
  }
  if (!json) {
    return <p className="mb-0">Nenhum campo detalhado.</p>;
  }
  return (
    <pre className="border rounded p-2 mb-0 bg-light small" style={{ whiteSpace: 'pre-wrap' }}>
      {json}
    </pre>
  );
}

function CelulaDiff({
  valor,
  variante,
}: {
  valor: string | undefined;
  variante: 'removido' | 'adicionado';
}) {
  if (valor == null) {
    return <span className="text-muted">—</span>;
  }

  const estilo =
    variante === 'removido'
      ? { background: '#ffebe9', color: '#82071e' }
      : { background: '#dafbe1', color: '#116329' };

  return (
    <span
      className="d-inline-block w-100 px-2 py-1 rounded-1 small"
      style={{ ...estilo, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      title={valor}
    >
      {variante === 'removido' ? `- ${valor}` : `+ ${valor}`}
    </span>
  );
}

function VisaoAlteracoesTabela({ linhas }: { linhas: LinhaDiffHistorico[] }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0 align-middle">
        <thead className="table-light">
          <tr>
            <th scope="col" style={{ width: '28%' }}>Campo</th>
            <th scope="col">Antes</th>
            <th scope="col">Depois</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr key={linha.campo}>
              <td className="small text-truncate" style={{ maxWidth: 160 }} title={linha.campo}>
                {linha.campo}
              </td>
              <td>
                <CelulaDiff valor={linha.removido} variante="removido" />
              </td>
              <td>
                <CelulaDiff valor={linha.adicionado} variante="adicionado" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VisaoAlteracoesEmpilhada({ linhas }: { linhas: LinhaDiffHistorico[] }) {
  return (
    <div className="border rounded p-2 bg-white">
      {linhas.map((linha) => (
        <div key={linha.campo} className="mb-2">
          <div className="fw-semibold small mb-1 text-truncate" title={linha.campo}>
            {linha.campo}
          </div>
          {linha.removido != null && (
            <pre
              className="mb-0 px-2 py-1 small"
              style={{
                whiteSpace: 'pre-wrap',
                background: '#ffebe9',
                color: '#82071e',
                borderLeft: '3px solid #ff8182',
              }}
              title={linha.removido}
            >
              {`- ${linha.removido}`}
            </pre>
          )}
          {linha.adicionado != null && (
            <pre
              className="mb-0 px-2 py-1 small"
              style={{
                whiteSpace: 'pre-wrap',
                background: '#dafbe1',
                color: '#116329',
                borderLeft: '3px solid #4ac26b',
              }}
              title={linha.adicionado}
            >
              {`+ ${linha.adicionado}`}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

function VisaoAlteracoes({
  log,
  estadoAnterior,
}: {
  log: HistoricoDto;
  estadoAnterior?: Record<string, unknown>;
}) {
  const linhas = linhasDiffHistorico(
    log.diff as Record<string, unknown> | null,
    log.acao,
    estadoAnterior,
  );

  if (log.acao === 'DELETE' && linhas.length === 0) {
    return <p className="mb-0">O registro foi removido.</p>;
  }
  if (linhas.length === 0) {
    return <p className="mb-0">Nenhuma alteração detalhada.</p>;
  }

  return linhasEmTabela(linhas)
    ? <VisaoAlteracoesTabela linhas={linhas} />
    : <VisaoAlteracoesEmpilhada linhas={linhas} />;
}

function CardLog({
  log,
  visao,
  estadoAnterior,
}: {
  log: HistoricoDto;
  visao: VisaoDetalhe;
  estadoAnterior?: Record<string, unknown>;
}) {
  return (
    <div className="border rounded p-3 mb-2">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-2 flex-wrap">
        <span className="d-inline-flex align-items-center gap-2">
          <IconeAcao acao={log.acao} />
          <span>{log.criadoPor?.username}</span>
        </span>
        <span className="text-muted text-nowrap small">{dataLinha(log.criadoEm)}</span>
      </div>
      {visao === 'atual'
        ? <VisaoAtual log={log} />
        : <VisaoAlteracoes log={log} estadoAnterior={estadoAnterior} />}
    </div>
  );
}

export default function ModalDetalheHistorico({ item, show, onHide }: ModalDetalheHistoricoProps) {
  const [logs, setLogs] = useState<HistoricoDto[]>([]);
  const [visao, setVisao] = useState<VisaoDetalhe>(lerVisaoSalva);

  useEffect(() => {
    if (!show || !item?.tabela || item.registro == null || item.registro === '') {
      setLogs([]);
      return;
    }

    let ativo = true;
    setLogs([item]);
    GetHistoricoUsuario({
      tabela: item.tabela,
      registro: String(item.registro),
      data_inicial: '2000-01-01',
      data_final: '3000-01-01',
      skip: 0,
      take: 5000,
      orderBy: 'DESC',
      orderField: 'criadoEm',
    })
      .then(([lista]) => {
        if (ativo && lista.length > 0) setLogs(lista);
      })
      .catch(() => {
        if (ativo) setLogs([item]);
      });

    return () => {
      ativo = false;
    };
  }, [show, item]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Log do Registro - {item?.registro} - {item ? rotuloTabela(item.tabela) : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="visao-detalhe-historico"
            checked={visao === 'alteracoes'}
            onChange={(e) => {
              const proxima: VisaoDetalhe = e.target.checked ? 'alteracoes' : 'atual';
              setVisao(proxima);
              salvarVisao(proxima);
            }}
          />
          <label className="form-check-label" htmlFor="visao-detalhe-historico">
            JSON / Diff
          </label>
        </div>

        {logs.map((log, indice) => (
          <CardLog
            key={`${log.id ?? log.criadoEm}-${indice}`}
            log={log}
            visao={visao}
            estadoAnterior={estadoAntesDoEvento(logs, indice)}
          />
        ))}
      </Modal.Body>
    </Modal>
  );
}
